import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTeamsDeepLink } from '@/hooks/useTeamsDeepLink';
import {
  UserCog, Wallet, PiggyBank, CalendarClock, PiggyBank as PiggyBankIcon, Archive,
  Settings, LineChart, Skull, FileSearch, BellRing,
} from '@retail-ops/panel-shell/fi';
import { TopBar } from './TopBar';
import { ModuleRail } from '@/components/primitives';
import { SupportReportButton } from '@/components/support/SupportReportButton';
import { FindCustomerModal } from '@/components/customer/FindCustomerModal';
import type { ModuleRailTab, PaletteItem } from '@/components/primitives';
import { MODULES, modulesForRole, modulesForRoles } from '@/constants/modules';
import { useAuth } from '@/contexts/AuthContext';
import { useApprovalsBadge } from '@/modules/RAP/hooks/useApprovalsBadge';
import { APP_VERSION, APP_BUILD_TIME, APP_SUBTITLE } from '@/constants/version';
import type { UserRole } from '@retail-ops/shared-types/enums';
import { DebugPanel } from './DebugPanel/DebugPanel';
import { useDebugPanelVisible } from './DebugPanel/hooks/useDebugPanelVisible';
import { useDebugPanelExpanded } from './DebugPanel/hooks/useDebugPanelExpanded';
import { ModeBanner } from '@/components/primitives/ModeBanner';
import { useSessionMode } from '@/hooks/useSessionMode';

export interface ShellLayoutProps {
  children: ReactNode;
  rightPanel?: ReactNode;
}

/**
 * Acct Maintenance decision tree (Bill, 2026-04-22): top-level choice is
 * "opening a new account" vs "maintaining an existing one". When
 * maintaining, pick the product family - CIF / DDA / SAV / CD / IRA / SDB.
 * Module codes that map to submenu items (RCM/RCD/RIM/RSM) are hidden from
 * the top rail so they only appear inside the Acct Maintenance dropdown -
 * their routes still work when navigated directly.
 */
const ACCT_MAINT_CHILD_CODES: ReadonlyArray<string> = ['RCM', 'RCD', 'RIM', 'RSM'];

/**
 * Admin submenu (Bill 2026-04-22): house the low-traffic back-office
 * surfaces — Reporting, Kill List, Audit, Notifications — under a single
 * Admin dropdown so the main rail stays focused on customer-facing work.
 * Each entry is role-gated via MODULES[].roles; an empty submenu means the
 * Admin tab is hidden entirely for that role.
 */
function buildAdminSubmenu(role: UserRole): ModuleRailTab[] {
  const submenu: ModuleRailTab[] = [];
  const rrm = MODULES.find((m) => m.code === 'RRM');
  if (rrm && rrm.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RRM', name: 'Reporting', path: rrm.path, icon: LineChart });
  }
  const rkl = MODULES.find((m) => m.code === 'RKL');
  if (rkl && rkl.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RKL', name: 'Kill List', path: rkl.path, icon: Skull });
  }
  const ras = MODULES.find((m) => m.code === 'RAS');
  if (ras && ras.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RAS', name: 'Audit', path: ras.path, icon: FileSearch });
  }
  const rnc = MODULES.find((m) => m.code === 'RNC');
  if (rnc && rnc.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RNC', name: 'Notifications', path: rnc.path, icon: BellRing });
  }
  // Wave 6.3 — SuperAdmin Records Management portal. Hard-delete + bulk
  // corrections + Purge History viewer + Recovery wizard + Test Mode wipe.
  // Hardcoded SuperAdmin gate — until role-claim auth lands (Wave 13), only
  // SuperAdmin role-gated entry is acceptable.
  if (role === 'SuperAdmin') {
    submenu.push({ code: 'RECORDS', name: 'Records Mgmt', path: '/super-admin/records', icon: FileSearch });
  }
  return submenu;
}

function buildAcctMaintenanceSubmenu(role: UserRole): ModuleRailTab[] {
  const submenu: ModuleRailTab[] = [];

  const rcm = MODULES.find((m) => m.code === 'RCM');
  if (rcm && rcm.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RCM', name: 'CIF', path: rcm.path, icon: UserCog });
  }
  submenu.push({ code: 'RAM_DDA', name: 'DDA', path: '/ram?product=DDA', icon: Wallet });
  submenu.push({ code: 'RAM_SAV', name: 'SAV', path: '/ram?product=SAV', icon: PiggyBank });
  const rcd = MODULES.find((m) => m.code === 'RCD');
  if (rcd && rcd.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RCD', name: 'CD', path: rcd.path, icon: CalendarClock });
  }
  const rim = MODULES.find((m) => m.code === 'RIM');
  if (rim && rim.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RIM', name: 'IRA', path: rim.path, icon: PiggyBankIcon });
  }
  const rsm = MODULES.find((m) => m.code === 'RSM');
  if (rsm && rsm.roles.some((r) => r.toLowerCase() === role.toLowerCase())) {
    submenu.push({ code: 'RSM', name: 'SDB', path: rsm.path, icon: Archive });
  }
  return submenu;
}

export function ShellLayout({ children, rightPanel }: ShellLayoutProps): JSX.Element {
  const { user } = useAuth();
  const sessionMode = useSessionMode();
  // Officer code + branch, compact. Deliberately NOT the UPN: the footer is on
  // screen in every screenshot a pilot user sends, and an email address there
  // is more identity than a support ticket needs.
  const footerWho = [user?.officerCode, user?.activeBranchCode ?? user?.branchCode]
    .filter(Boolean)
    .join(' · ');
  const role = user?.primaryRole ?? 'Officer';
  const navigate = useNavigate();
  // Message / card links ("cart/<id>") open that cart (Bill 2026-10-14).
  useTeamsDeepLink();
  const [paletteOpen, setPaletteOpen] = useState<boolean>(false);
  // Debug panel gating — read here so the shell grid can reserve the row only
  // for SuperAdmin / Test Mode users. Non-eligible users see the original
  // 4-row layout exactly as before (no behavioral change).
  const debugVisible = useDebugPanelVisible();
  const { expanded: debugExpanded } = useDebugPanelExpanded();

  // Union over every role the person holds (primary role alone hid New Acct from Executive+Officer users).
  const allRoles = user?.roles && user.roles.length > 0 ? user.roles : [role];
  const visible = modulesForRoles(allRoles).filter(
    (m) => !ACCT_MAINT_CHILD_CODES.includes(m.code)
  );

  // Pending-approvals badge for the Approvals tab. Polls every 60s; tone flips
  // to 'warn' (red ring) when any pending approval is urgent or SLA-breaching.
  // Bill 2026-04-24: badge is part of "fully surface Approvals INSIDE the app".
  const approvalsBadge = useApprovalsBadge();

  const tabs: ModuleRailTab[] = visible.map((m) => {
    if (m.code === 'RAM') {
      return {
        code: m.code,
        name: m.name,
        path: m.path,
        icon: m.icon,
        submenu: buildAcctMaintenanceSubmenu(role),
      };
    }
    if (m.code === 'RAP') {
      const tab: ModuleRailTab = { code: m.code, name: m.name, path: m.path, icon: m.icon };
      if (approvalsBadge.count > 0) {
        tab.badge = approvalsBadge.count;
        tab.badgeTone = approvalsBadge.urgent ? 'warn' : 'default';
      }
      return tab;
    }
    return { code: m.code, name: m.name, path: m.path, icon: m.icon, ...(m.activePrefix ? { activePrefix: m.activePrefix } : {}) };
  });

  // Operations tab REMOVED from the rail (Bill 2026-07-13 Items 1/3 — pilot
  // FSRs get a streamlined ribbon; the 5 operational panels at /admin remain
  // reachable by direct URL for Admin/SuperAdmin until the surface is fully
  // fleshed out). buildAdminSubmenu still gates the /admin routes themselves.

  // Command palette items - modules flat (incl. Acct Maint children inline so
  // typing "cif" jumps straight to /rcm without the dropdown). Future: prepend
  // recent customers + active carts.
  const paletteItems: PaletteItem[] = useMemo(() => {
    const roleModules = modulesForRole(role);
    const items: PaletteItem[] = roleModules.map((m, i) => ({
      id: m.code,
      label: m.name === 'CIF' ? 'CIF Update' : m.name,
      sublabel: m.scope.split('.')[0],
      icon: m.icon,
      shortcut: i < 9 ? `Alt+${i + 1}` : undefined,
      onSelect: () => navigate(m.path),
    }));
    return items;
  }, [role, navigate]);

  // Global keyboard shortcuts:
  //   Alt+1..9   - jump to Nth module rail tab
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // PILOT 2026-09-11 (Bill): Ctrl/Cmd+K no longer opens the command
      // palette - it is off for the pilot along with the header search
      // trigger. The palette code stays; only its two entry points are gone.
      // Alt+N module jump - only if focus isn't in an input (avoid clashing
      // with inline data entry).
      if (e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey && /^[1-9]$/.test(e.key)) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
        const idx = Number(e.key) - 1;
        const tab = tabs[idx];
        if (tab) {
          e.preventDefault();
          navigate(tab.path.split('?')[0] ?? tab.path);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, tabs]);

  const handleOpenPalette = useCallback(() => setPaletteOpen(true), []);
  const handleClosePalette = useCallback(() => setPaletteOpen(false), []);

  // Find-customer modal (Bill 2026-07-13 Item 6) — a real global customer
  // lookup, distinct from the module-jump palette. Opened via the custom
  // 'rop:find-customer' window event (QuickActionsRow / TopBar dispatch it).
  const [findCustomerOpen, setFindCustomerOpen] = useState<boolean>(false);
  useEffect(() => {
    const open = (): void => setFindCustomerOpen(true);
    window.addEventListener('rop:find-customer', open);
    return () => window.removeEventListener('rop:find-customer', open);
  }, []);

  const shellClasses = [
    'shell',
    debugVisible ? 'shell--debug-visible' : '',
    debugVisible && debugExpanded ? 'shell--debug-expanded' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={shellClasses}>
      <TopBar onOpenPalette={handleOpenPalette} />
      <ModuleRail tabs={tabs} />
      <div className={`shell__body ${rightPanel ? 'shell__body--with-right-panel' : ''}`.trim()}>
        <main className="shell__main" role="main">
          {/* W6 2026-10-07: Sandbox/Pilot strip on every screen (sticky at the
              top of the scroll area; a 0-height placeholder in Production). */}
          <ModeBanner mode={sessionMode} />
          {children}
        </main>
        {rightPanel}
      </div>
      {debugVisible && (
        <div className="shell__debug-row">
          <SupportReportButton />
          <DebugPanel />
        </div>
      )}
      {/* "Report a problem" for EVERY user (bottom-left lifebuoy, fixed position). It only rendered in the
          debug row, which shows only when global Test Mode is on - so pilot users could not report anything
          (found 2026-10-07 before the Phase 1 pilot announcement). */}
      {!debugVisible && <SupportReportButton />}
      {/* Footer (0.157.2 phone pass): on a phone (<= 640px) only the short
          line shows - "PCB · v<version> · <officer> · <branch>" plus the icon
          credit - on ONE line, in normal grid flow, with the Report-a-problem
          lifebuoy docked at its left end (layout.css). The wide spans are the
          desktop footer, unchanged. Full detail lives in Help -> About. */}
      <footer className="shell__footer" role="contentinfo">
        <span className="shell__footer-wide">{APP_SUBTITLE}</span>
        <span className="shell__footer-wide" aria-hidden>·</span>
        <span className="shell__footer-wide">Employee-Owned · Member FDIC</span>
        <span className="shell__footer-wide" aria-hidden>·</span>
        {/* SUPPORT FINGERPRINT (Bill 2026-09-11). A pilot user's screenshot
            should answer "who, where, which build" without anyone having to
            ask. Officer code + branch are short enough to stay unobtrusive and
            are exactly the two facts a support request always lacks. Full
            detail lives in Help -> About. */}
        <span className="shell__footer-wide">
          v{APP_VERSION} · {APP_BUILD_TIME}
          {footerWho ? ` · ${footerWho}` : ''}
        </span>
        <span className="shell__footer-narrow">
          PCB · v{APP_VERSION}
          {footerWho ? ` · ${footerWho}` : ''}
        </span>
        <span aria-hidden>·</span>
        <span className="shell__footer-credit">
          <span className="shell__footer-wide">Icons: </span>
          <a
            href="https://www.flaticon.com/uicons"
            target="_blank"
            rel="noreferrer noopener"
          >
            Uicons by Flaticon
          </a>
        </span>
      </footer>
      <FindCustomerModal open={findCustomerOpen} onClose={() => setFindCustomerOpen(false)} />
    </div>
  );
}
